import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, CheckCircle, Clock, Building2, ArrowRight, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const content = {
  pt: {
    title: "Período de avaliação encerrado",
    subtitle: "Seu período de 7 dias gratuitos chegou ao fim.",
    description: "Para continuar utilizando o OmbudNow e gerenciar denúncias e ouvidoria da sua empresa, escolha um dos planos abaixo.",
    features: {
      title: "O que você terá acesso:",
      items: [
        "Canal de denúncias anônimas",
        "Canal de ouvidoria",
        "Dashboard de gestão",
        "Relatórios e métricas",
        "Suporte por email"
      ]
    },
    monthly: "/mês",
    selectPlan: "Selecionar",
    processing: "Processando...",
    contact: "Dúvidas? Entre em contato:",
    manageSubscription: "Gerenciar assinatura existente",
    plans: {
      essential: {
        name: "Essencial",
        price: "R$ 89,00",
        description: "1–10 colaboradores",
        features: ["7 dias grátis", "5 registros/mês", "Suporte via Email"]
      },
      professional: {
        name: "Profissional",
        price: "R$ 199,00",
        description: "11–100 colaboradores",
        features: ["7 dias grátis", "20 registros/mês", "Suporte via Email"],
        recommended: true
      },
      enterprise: {
        name: "Empresarial",
        price: "R$ 499,00",
        description: "+100 colaboradores",
        features: ["7 dias grátis", "Registros ilimitados", "Suporte Personalizado", "Opção 0800"]
      }
    }
  },
  en: {
    title: "Trial period ended",
    subtitle: "Your 7-day free trial has ended.",
    description: "To continue using OmbudNow and manage your company's reports and ombudsman, choose one of the plans below.",
    features: {
      title: "What you'll have access to:",
      items: [
        "Anonymous whistleblowing channel",
        "Ombudsman channel",
        "Management dashboard",
        "Reports and metrics",
        "Email support"
      ]
    },
    monthly: "/month",
    selectPlan: "Select",
    processing: "Processing...",
    contact: "Questions? Contact us:",
    manageSubscription: "Manage existing subscription",
    plans: {
      essential: {
        name: "Essential",
        price: "US$ 29.00",
        description: "1–10 employees",
        features: ["7-day free trial", "5 reports/month", "Email Support"]
      },
      professional: {
        name: "Professional",
        price: "US$ 49.00",
        description: "11–100 employees",
        features: ["7-day free trial", "20 reports/month", "Email Support"],
        recommended: true
      },
      enterprise: {
        name: "Enterprise",
        price: "US$ 129.00",
        description: "100+ employees",
        features: ["7-day free trial", "Unlimited reports", "Personalized Support", "0800 Option"]
      }
    }
  }
};

export default function TrialExpiredScreen({ empresa, slug }) {
  const { i18n } = useTranslation();
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const currentLang = i18n.language || 'pt';
  const t = content[currentLang] || content.pt;

  const handleCheckout = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const response = await axios.post(`${API_URL}/stripe/checkout`, {
        plan_id: packageId,
        origin_url: window.location.origin,
        empresa_id: empresa?.id,
        user_email: empresa?.admin_email
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error starting checkout' : 'Erro ao iniciar checkout');
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!empresa?.id) return;
    setPortalLoading(true);
    try {
      const response = await axios.post(`${API_URL}/stripe/portal`, {
        empresa_id: empresa.id,
        return_url: window.location.href
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(currentLang === 'en' ? 'Error opening portal' : 'Erro ao abrir portal');
      console.error('Portal error:', error);
    } finally {
      setPortalLoading(false);
    }
  };

  const plans = [
    { 
      id: currentLang === 'en' ? 'essential_usd' : 'essencial_brl',
      ...t.plans.essential 
    },
    { 
      id: currentLang === 'en' ? 'professional_usd' : 'profissional_brl',
      ...t.plans.professional 
    },
    { 
      id: currentLang === 'en' ? 'enterprise_usd' : 'empresarial_brl',
      ...t.plans.enterprise 
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#f5a623]/20 rounded-full mb-6">
            <Lock className="w-10 h-10 text-[#f5a623]" />
          </div>
          
          {empresa?.nome && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-slate-400" />
              <span className="text-slate-400">{empresa.nome}</span>
            </div>
          )}
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {t.title}
          </h1>
          <p className="text-xl text-[#f5a623] mb-4">{t.subtitle}</p>
          <p className="text-slate-400 max-w-2xl mx-auto">
            {t.description}
          </p>
        </div>

        {/* Features Preview */}
        <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-6 mb-10">
          <h3 className="text-white font-semibold mb-4">{t.features.title}</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {t.features.items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#f5a623] flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`relative rounded-2xl p-6 transition-all ${
                plan.recommended 
                  ? 'bg-gradient-to-b from-[#f5a623]/20 to-[#f5a623]/5 border-2 border-[#f5a623] transform md:scale-105' 
                  : 'bg-white/5 backdrop-blur border border-white/10 hover:border-white/20'
              }`}
            >
              {plan.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#f5a623] text-[#1a1a2e] px-4 py-1 rounded-full text-xs font-semibold">
                  Recomendado
                </div>
              )}
              
              <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
              <p className="text-slate-400 text-sm mb-4">{plan.description}</p>
              
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-slate-400 text-sm">{t.monthly}</span>
              </div>
              
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#f5a623] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button 
                className={`w-full ${
                  plan.recommended 
                    ? 'bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e]' 
                    : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                }`}
                onClick={() => handleCheckout(plan.id)}
                disabled={checkoutLoading === plan.id}
                data-testid={`btn-select-${plan.id}`}
              >
                {checkoutLoading === plan.id ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    {t.processing}
                  </>
                ) : (
                  <>
                    {t.selectPlan}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className="text-center space-y-4">
          {empresa?.stripe_customer_id && (
            <Button 
              variant="outline"
              onClick={handleManageSubscription}
              disabled={portalLoading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Settings className="w-4 h-4 mr-2" />
              {portalLoading ? t.processing : t.manageSubscription}
            </Button>
          )}
          <p className="text-slate-500 text-sm">
            {t.contact} <a href="mailto:suporte@ombudnow.com" className="text-[#f5a623] hover:underline">suporte@ombudnow.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}
